import type { EventReanalysis } from "@sitescene/types";

import { useState } from "react";
import { useNavigate } from "react-router";

import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel } from "@/components/detail-panel";
import { Button, Field, Select, TextInput } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { reanalysisEventPath } from "@/constants/routes";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";
import {
	classNames,
	nameClass,
} from "@/features/reanalysis/describe-reanalysis";
import {
	type ReanalysisFilters,
	useReanalyses,
} from "@/features/reanalysis/use-reanalysis";
import { formatSeverityChange, formatWhen } from "@/pages/plausibility/format";
import { parseEventId, refId } from "@/utils/ids";

import { KIND_LABELS, OUTCOME_LABELS, STATUS_LABELS } from "./format";

/** The list's filters: the field each sets, and its options by value. */
const FILTERS: readonly {
	field: keyof ReanalysisFilters;
	label: string;
	options: Record<string, string>;
}[] = [
	{ field: "status", label: "Status", options: STATUS_LABELS },
	{ field: "outcome", label: "Outcome", options: OUTCOME_LABELS },
	{ field: "kind", label: "Kind", options: KIND_LABELS },
];

/**
 * Every reanalysis in the selected organization, from either app, newest
 * first: what staff said the clip showed, whether the footage bore it out,
 * and what it did to the event. Correcting an event starts here too, from
 * its ID or a dashboard link.
 */
export const Reanalyses = () => {
	const [filters, setFilters] = useState<ReanalysisFilters>({});
	const navigate = useNavigate();
	const query = useReanalyses(filters);
	const taxonomy = useTaxonomy();
	const names = classNames(taxonomy.data);
	const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

	const columns: Column<EventReanalysis>[] = [
		{
			header: "Requested",
			key: "requested",
			render: (row) => formatWhen(row.requestedAt),
		},
		{
			header: "Event",
			key: "event",
			render: (row) =>
				row.before.title ?? <span className="text-tertiary">Untitled</span>,
		},
		{ header: "Kind", key: "kind", render: (row) => KIND_LABELS[row.kind] },
		{
			header: "Target class",
			key: "target",
			render: (row) => nameClass(row.targetClassId, names),
		},
		{
			header: "Status",
			key: "status",
			render: (row) => (
				<Badge label={STATUS_LABELS[row.status]} value={row.status} />
			),
		},
		{
			header: "Outcome",
			key: "outcome",
			render: (row) => (
				<Badge
					label={row.outcome && OUTCOME_LABELS[row.outcome]}
					value={row.outcome}
				/>
			),
		},
		{
			header: "Staff action",
			key: "override",
			render: (row) =>
				row.forced || row.reverted ? (
					<span className="flex flex-wrap gap-1">
						{row.forced && <Badge label="Forced" value="forced" />}
						{row.reverted && <Badge label="Reverted" value="reverted" />}
					</span>
				) : (
					<span className="text-tertiary">—</span>
				),
		},
		{
			header: "Severity",
			key: "severity",
			render: (row) =>
				formatSeverityChange(row.before.severity, row.after?.severity),
		},
		{
			header: "Requested by",
			key: "requestedBy",
			render: (row) =>
				row.requestedByName ?? (
					<code className="text-xs">{row.requestedBy}</code>
				),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<FindEvent />

			<form
				aria-label="Filter reanalyses"
				className="flex flex-wrap items-end gap-3"
				onSubmit={(event) => event.preventDefault()}
			>
				{FILTERS.map(({ field, label, options }) => (
					<Field htmlFor={`filter-${field}`} key={field} label={label}>
						<Select
							id={`filter-${field}`}
							onChange={(e) =>
								setFilters((f) => ({
									...f,
									[field]: e.target.value || undefined,
								}))
							}
							value={filters[field] ?? ""}
						>
							<option value="">Any</option>
							{Object.entries(options).map(([value, text]) => (
								<option key={value} value={value}>
									{text}
								</option>
							))}
						</Select>
					</Field>
				))}
			</form>

			<div>
				<StatusMessage
					empty="No reanalyses here. Find an event above to correct one."
					error={query.error}
					isEmpty={!query.isLoading && rows.length === 0}
					isLoading={query.isLoading}
					loading="Loading reanalyses…"
				/>
				{rows.length > 0 && (
					<>
						<DataTable
							columns={columns}
							onRowClick={(row) =>
								void navigate(reanalysisEventPath(refId(row.event)))
							}
							rowKey={(row) => row.id}
							rows={rows}
						/>
						{query.hasNextPage && (
							<div className="mt-3">
								<Button
									disabled={query.isFetchingNextPage}
									onClick={() => void query.fetchNextPage()}
									type="button"
									variant="secondary"
								>
									{query.isFetchingNextPage ? "Loading…" : "Load more"}
								</Button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

/**
 * For when a customer sends staff an event link. Opens the event page, which
 * says whether the event is in this organization.
 */
const FindEvent = () => {
	const navigate = useNavigate();
	const [input, setInput] = useState("");
	const eventId = parseEventId(input);

	return (
		<DetailPanel title="Find event">
			<form
				className="flex flex-wrap items-end gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					if (!eventId) return;
					void navigate(reanalysisEventPath(eventId));
				}}
			>
				<Field htmlFor="find-event-id" label="Event ID or link">
					<TextInput
						className="w-80 max-w-full"
						id="find-event-id"
						onChange={(e) => setInput(e.target.value)}
						placeholder="66f1c0…"
						value={input}
					/>
				</Field>
				<Button disabled={!eventId} type="submit">
					Find
				</Button>
			</form>
		</DetailPanel>
	);
};
